import React, { useState } from 'react';
import { Container, Card, Typography, TextField, FormControlLabel, Checkbox, Button, Box, Stack, AlertColor, AlertTitle, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import PublicApiService from '../Services/publicApiService.ts';

const Under18ConsentForm = () => {
  // Initial state to reset form
  const initialFormState = {
    firstName: '',
    lastName: '',
    placement: '',
    minorFirstName: '',
    minorLastName: '',
    email: '',
    phone: '',
    relationship: '',
    initials: {},
    signature: '',
    notaryName: '',
    notarySignature: '',
    date: ''
  };

  const [formData, setFormData] = useState(initialFormState);
  const [phoneError, setPhoneError] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{
      open: boolean;
      message: string;
      severity: AlertColor;
    }>({
      open: false,
      message: "",
      severity: "warning",
    });

  const handlePhoneChange = (e) => {
    let phoneNumber = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters

    // Prevent deletion of "+1"
    if (phoneNumber.startsWith('1')) {
      phoneNumber = phoneNumber.slice(1);
    }

    // Ensure exactly 10 digits
    if (phoneNumber.length > 10) {
      phoneNumber = phoneNumber.slice(0, 10);
    }

    // Validate phone number length
    if (phoneNumber.length !== 10) {
      setPhoneError('Phone number must be exactly 10 digits.');
    } else {
      setPhoneError('');
    }

    setFormData({ ...formData, phone: phoneNumber });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name !== 'phone') {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleInitials = (key, label) => {
    setFormData({
      ...formData,
      initials: {
        ...formData.initials,
        [key]: { checked: !formData.initials[key]?.checked, label }
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (phoneError) {
      setAlert({ open: true, message: "Please enter a valid 10-digit phone number.", severity: "error" });
      return;
    }

    try {
      setSubmitting(true);
      const apiService = new PublicApiService();
      await apiService.postConsentData(formData);
      console.log("Form Submitted:", formData);

      // ✅ Reset form fields after submission
      setFormData(initialFormState);
      setSuccessModalOpen(true);
    } catch (error) {
      setAlert({ open: true, message: "Error submitting the form. Please try again.", severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      {alert.open && (
          <Stack sx={{ width: '100%' }} spacing={2}>
            <Alert 
              severity={alert.severity}  
              onClose={() => setAlert({ ...alert, open: false })} 
            >
              <AlertTitle>{alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}</AlertTitle>
              {alert.message}
            </Alert>
          </Stack>
      )}
      <Card sx={{ p: 4, boxShadow: 3 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Minor Consent for Body Piercing
        </Typography>

        {/* Minor's Information */}
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2} direction="row">
            <TextField fullWidth label="Minor's First Name" name="minorFirstName" value={formData.minorFirstName} onChange={handleChange} required />
            <TextField fullWidth label="Minor's Last Name" name="minorLastName" value={formData.minorLastName} onChange={handleChange} required />
          </Stack>
          <TextField fullWidth label="Placement of Piercing" name="placement" value={formData.placement} onChange={handleChange} required sx={{ mt: 2 }} />

          {/* Parent/Guardian Section */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>Parent/Guardian Information</Typography>
          <Stack spacing={2} direction="row">
            <TextField fullWidth label="Guardian's First Name" name="firstName" value={formData.firstName} onChange={handleChange} required />
            <TextField fullWidth label="Guardian's Last Name" name="lastName" value={formData.lastName} onChange={handleChange} required />
          </Stack>
          <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
            <TextField fullWidth label="Guardian's Email" type="email" name="email" value={formData.email} onChange={handleChange} required />
            <TextField 
              fullWidth 
              label="Guardian's Phone Number" 
              type="tel" 
              name="phone" 
              value={formData.phone ? `+1 ${formData.phone}` : '+1 '} 
              onChange={handlePhoneChange} 
              required 
              error={Boolean(phoneError)}
              helperText={phoneError} 
            />
          </Stack>
          <TextField fullWidth label="Relationship to Minor" name="relationship" value={formData.relationship} onChange={handleChange} required sx={{ mt: 2 }} />

          {/* Acknowledgments */}
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Please review and acknowledge each statement by checking the box:</Typography>
          <Stack spacing={2}>
            {[
              'I, the parent/legal guardian, consent to my minor child receiving a body piercing procedure.',
              'I acknowledge that the risks of infection, swelling, and rejection have been explained.',
              'I understand that I must be present with valid ID during the procedure.',
              'I confirm that I have provided proper identification and proof of guardianship if required.',
              'I am aware that I am consenting to a tattoo and/or body piercing procedure.',
              'I accept the risks including infection, rejection, swelling, pain, misalignment, scarring, and other effects.',
              'I understand that once the procedure supplies are opened, I will be charged in full.',
              'I understand that tattooing and body piercing is not an exact science and results may vary.',
              'I understand tattoos on hands, feet, face & lips are not guaranteed.',
              'I acknowledge that the shop is not responsible for healing issues after leaving.',
              'I agree to check and tighten body jewelry at least once a month.',
              'I confirmed the drawing, stencil, and placement before the procedure.',
              'I agree to hold JASEL PIERCING LLC harmless for any unforeseen consequences.',
              '**I consent to receiving communication, including appointment reminders, and follow-ups, via text message and phone calls at the number provided.**',
              'I agree not to bring any legal action or claim against Jasel Jewelry, its owners, employees, contractors, agents, affiliated companies, or associated personnel for issues arising from or related to my voluntary decision to receive tattoo and/or body piercing services at this establishment, except to the extent such waiver or limitation is prohibited by applicable law.'
            ].map((item, index) => (
              <Card key={index} sx={{ display: 'flex', alignItems: 'center', p: 2, boxShadow: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.initials[index]?.checked || false}
                      onChange={() => handleInitials(index, item)}
                      required
                      sx={{ mr: 2 }}
                    />
                  }
                  label={item}
                  sx={{ flexGrow: 1 }}
                />
              </Card>
            ))}
          </Stack>

          {/* Signatures Section */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>Signatures</Typography>
          <TextField fullWidth label="Parent/Guardian Signature" name="signature" value={formData.signature} onChange={handleChange} required placeholder="Type full name as signature" sx={{ mt: 2 }} />

          {/* Notary Public Section */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>Notary Public Section (If Required)</Typography>
          <Stack spacing={2} direction="row">
            <TextField fullWidth label="Notary Name" name="notaryName" value={formData.notaryName} onChange={handleChange} />
            <TextField fullWidth label="Notary Signature" name="notarySignature" value={formData.notarySignature} onChange={handleChange} />
          </Stack>

          {/* Submit Button */}
          <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 3 }} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Consent Form'}
          </Button>
        </Box>
      </Card>
      {/* Success Modal */}
      <Dialog
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        aria-labelledby="success-dialog-title"
      >
        <DialogTitle id="success-dialog-title">✅ Form Submitted</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your consent form has been submitted successfully! Thank you.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessModalOpen(false)} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Under18ConsentForm;
